import React, { useEffect, useRef, useState } from 'react';
import Testimonials from '../Testmonials';
import loadTestMonials from '../TestMonial-loader';
import engagebayBaseAPI from '../api';
import { v4 as uuidv4 } from 'uuid';
import { LoginDeps } from './LoginApp';
import { clearResendMeta } from './MfaScreen';
import SocialOauthButtons, { buildMicrosoftOauthHref } from '../commons/auth/SocialOauthButtons';
import { withOAuthReturn } from '../commons/util/activateFlow';
import ShopifyAuthModal from '../commons/auth/ShopifyAuthModal';

interface LoginScreenProps {
    deps: LoginDeps;
    onMfaRequired: (securityType: string, redirectBase: string | null, email?: string) => void;
    onSuccess: (exchangeToken: string | null, redirectBase: string | null, loginInfo?: any) => void;
}

type ErrorState =
    | { kind: 'text'; message: string }
    | { kind: 'account_not_activated'; email: string; userId: string }
    | { kind: 'invalid_code' }
    | null;

function isMobileApp() {
    return (
        typeof (window as any).Android !== 'undefined' ||
        (typeof (window as any).webkit !== 'undefined' &&
            typeof (window as any).webkit.messageHandlers !== 'undefined')
    );
}

/**
 * Credentials screen — renders email/password form, OAuth buttons, Testimonials panel.
 * Owns: form state, fingerprint, device UUID, autoLogin, Shopify modal, error display.
 * Does not render any OTP-related UI — that belongs entirely in MfaScreen.
 *
 * Why onSuccess/onMfaRequired callbacks: LoginApp owns the step transition and final
 * navigation; LoginScreen reports outcomes upward without knowing what happens next.
 */
const Login: React.FC<LoginScreenProps> = ({ deps, onMfaRequired, onSuccess }) => {
    const prefs = deps.prefs;
    const [email, setEmail] = useState<string>(deps.email || '');
    const [password, setPassword] = useState<string>('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<ErrorState>(
        deps.error ? { kind: 'text', message: deps.error } : null
    );
    const fingerprintRef = useRef('');
    const deviceUuidRef = useRef('');

    // Initialize device UUID from localStorage (persists across logins to track known devices)
    useEffect(() => {
        let uuid = localStorage.getItem('device_uuid');
        if (!uuid) {
            uuid = uuidv4();
            localStorage.setItem('device_uuid', uuid);
        }
        deviceUuidRef.current = uuid;
    }, []);

    // Load FingerprintJS2 and compute stable browser hash for device fingerprinting
    useEffect(() => {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/fingerprintjs2/2.1.4/fingerprint2.min.js';
        script.onload = () => fillBrowserFingerprint(fingerprintRef);
        document.head.appendChild(script);
    }, []);

    // Hydrate testimonials skeleton with current testimonial data after mount
    useEffect(() => {
        if (!prefs?.isClient) {
            loadTestMonials();
        }
    }, []);

    // autoLogin: backend passes ?login=yes to trigger automatic credential submission (partner SSO)
    useEffect(() => {
        if (deps.autoLogin === 'yes') {
            const savedPwd = localStorage.getItem('_eb_login_page');
            if (savedPwd) {
                setPassword(savedPwd);
                setTimeout(() => submitLogin(deps.email || '', savedPwd), 1000);
            }
        }
    }, []);

    // Mobile webview: delete stored token on login page load
    useEffect(() => {
        if (!isMobileApp()) return;
        const android = (window as any).Android;
        if (android) {
            android.deleteUserToken();
        } else {
            const wk = (window as any).webkit;
            if (wk?.messageHandlers?.doStuffMessageHandler) {
                try {
                    wk.messageHandlers.doStuffMessageHandler.postMessage(
                        JSON.stringify({ action: 'deleteUserToken' })
                    );
                } catch {
                    // native context not ready
                }
            }
        }
    }, []);

    async function submitLogin(em: string, pwd: string) {
        if (!em || !pwd) return;
        setError(null);
        setLoading(true);

        const payload: Record<string, unknown> = {
            email: em,
            password: pwd,
            fingerprint: fingerprintRef.current,
            device_uuid: deviceUuidRef.current,
        };
        if (localStorage.getItem('rem_goc')) {
            payload.rem_goc = true;
        }
        if (isMobileApp()) {
            payload.mobile_app_login = true;
        }
        try {
            const res = await engagebayBaseAPI.post('/rest/api/login/user-login-v3', payload, {
                headers: { 'x-source': 'browser' },
            });
            const data = res.data;

            if (res.status === 202 || data?.status === 'mfa_required') {
                setLoading(false);
                // Clear stale resend counter so MfaScreen always mounts at 0 attempts.
                // Without this, a leftover localStorage value from a previous session
                // shows wrong "N of 3 resends remaining" count before the user has done anything.
                clearResendMeta();
                onMfaRequired(data?.securityType, data?.redirect_base || null, email);
                return;
            }
            
            onSuccess(data?.exchange_token || null, data?.redirect_base || null, data);
        } catch (err: unknown) {
            setLoading(false);
            const errData = (err as any)?.response?.data;
            if (errData && typeof errData === 'object') {
                if (errData.type === 'ACCOUNT_NOT_ACTIVATED') {
                    setError({ kind: 'account_not_activated', email: errData.email, userId: errData.id });
                } else if (errData.type === 'INVALID_VERIFICATION_CODE') {
                    setError({ kind: 'invalid_code' });
                } else {
                    if ((err as any).status === 429) {
                        setError({ kind: 'text', message: getParsedText(errData.message || errData) });
                        return;
                    }
                    setError({ kind: 'text', message: String(errData.message || errData) });
                }
            } else {
                // Plain string body (e.g. "The username or password you entered is incorrect.")
                const msg = typeof errData === 'string' && errData
                    ? errData
                    : (err instanceof Error) ? err.message : String(err);
                if ((err as any).status === 429) {
                    setError({ kind: 'text', message: getParsedText(msg) });
                    return;
                }
                setError({ kind: 'text', message: msg });
            }
        }
    }

    function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        submitLogin(email, password);
    }

    function handleShopifySubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();
        const shopName = (e.currentTarget.elements.namedItem('shop') as HTMLInputElement)?.value;
        if (shopName) {
            window.location.href = withOAuthReturn(
                '/oauth?service=shopify_oauth&shopify_store_name=' + shopName);
        }
    }

    async function handleResendActivation(userId: string) {
        try {
            await engagebayBaseAPI.get('/rest/api/login/resend-verification-email', {
                params: { user_id: userId },
            });
            setError({ kind: 'text', message: 'Confirmation email sent successfully.' });
        } catch {
            setError({ kind: 'text', message: 'Unable to send confirmation email.' });
        }
    }

    // withOAuthReturn carries an in-flight OAuth consent flow across every link
    // that leaves this page — the signup hand-off and both social providers.
    const googleOauthUrl = withOAuthReturn(isMobileApp()
        ? '/oauth?mobile_app_login=true'
        : `${deps.appURL || ''}oauth?service=google`);

    const getParsedText = function(text: any) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');
        return doc.body.textContent;
    }    

    return (
        <>
            <div className={`user-verification-noty ${error ? '' : 'hide'}`}>
                <div className="content">
                    {error?.kind === 'text' && <span>{error.message}</span>}
                    {error?.kind === 'account_not_activated' && (
                        <span>
                            Your account is not activated. A confirmation email was sent to{' '}
                            <strong>{error.email}</strong>
                            <button
                                type="button"
                                className="btn btn-sm btn-danger ms-3"
                                onClick={() => handleResendActivation(error.userId)}
                            >
                                Resend Confirmation
                            </button>
                        </span>
                    )}
                    {error?.kind === 'invalid_code' && (
                        <span>Invalid verification code. Please try again.</span>
                    )}
                </div>
            </div>

            <section className="login-main-bg">
                <div className="login-video">
                    <object
                        style={{ background: 'none' }}
                        className="login-video-autoplay"
                        id="login-img-static"
                        type="image/svg+xml"
                        data="https://d2p078bqz5urf7.cloudfront.net/images/login/login-bg-animation.html"
                    />
                </div>

                <div id="content-container" className={`container h-100 ${error ? '' : 'mt-50'}`}>
                    <div className="row h-100">
                        <div className="col-12 col-lg-5">
                            {!prefs?.isClient && <Testimonials />}
                        </div>
                        <div className="col-12 col-lg-5">
                            <div className="card form-info-card login-form-right">
                                <div className="pb-4 mb-2 text-center form-info-header">
                                    <a
                                        href={prefs?.isClient ? '#' : 'https://www.engagebay.com/'}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="mb-4 logo-brand d-inline-block"
                                    >
                                        <img
                                            className="logoimg in-iframe-hide"
                                            src={prefs?.bigLogo}
                                            alt="Logo"
                                            height="32"
                                        />
                                    </a>
                                    <h3 className="pb-1 mb-2 fw-500 text-dark">Login</h3>
                                    <span
                                        className={`text-muted form-info-desc ${prefs?.isClient ? 'hide' : ''}`}
                                    >
                                        Don't have an account yet?{' '}
                                        <a
                                            href={withOAuthReturn(`/signup${deps.usersData ? '?' + deps.usersData : ''}`)}
                                            className="auth-switch-link"
                                        >
                                            Sign up
                                        </a>
                                    </span>
                                </div>

                                <form id="loginForm" name="loginForm" onSubmit={handleSubmit}>
                                    <div className="form-group">
                                        <input
                                            className="form-control"
                                            type="email"
                                            title="Enter a valid email address."
                                            pattern="^((([A-Za-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[ -퟿豈-﷏ﷰ-￯])+(\.([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[ -퟿豈-﷏ﷰ-￯])+)*)|((\x22)((((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(([\x01-\x08\x0b\x0c\x0e-\x1f\x7f]|\x21|[\x23-\x5b]|[\x5d-\x7e]|[ -퟿豈-﷏ﷰ-￯])|(\\([\x01-\x09\x0b\x0c\x0d-\x7f]|[ -퟿豈-﷏ﷰ-￯]))))*(((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(\x22)))@((([a-z]|\d|[ -퟿豈-﷏ﷰ-￯])|(([a-z]|\d|[ -퟿豈-﷏ﷰ-￯])([a-z]|\d|-|\.|_|~|[ -퟿豈-﷏ﷰ-￯])*([a-z]|\d|[ -퟿豈-﷏ﷰ-￯])))\.)+(([a-z]|[ -퟿豈-﷏ﷰ-￯])|(([a-z]|[ -퟿豈-﷏ﷰ-￯])([a-z]|\d|-|\.|_|~|[ -퟿豈-﷏ﷰ-￯])*([a-z]|[ -퟿豈-﷏ﷰ-￯])))$"
                                            name="email"
                                            autoComplete="email"
                                            placeholder="Work Email"
                                            required
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                        />
                                    </div>
                                    <div className="form-group position-relative password-field-group">
                                        <input
                                            id="password-field"
                                            className="form-control form-control-lg toggle-password"
                                            type={showPassword ? 'text' : 'password'}
                                            name="password"
                                            minLength={4}
                                            maxLength={25}
                                            autoComplete="off"
                                            placeholder="Password"
                                            required
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                        />
                                        <div className="cursor-pointer position-absolute top-50 end-0 translate-middle-y pe-3">
                                            <a
                                                href="#"
                                                className="pwd-field-icon-underline text-decoration-none login-eye-icon"
                                                onClick={(e) => { e.preventDefault(); setShowPassword((v) => !v); }}
                                            >
                                                <svg
                                                    id="eye-icon"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    xmlnsXlink="http://www.w3.org/1999/xlink"
                                                    version="1.1"
                                                    x="0px"
                                                    y="0px"
                                                    viewBox="0 0 50 50"
                                                    xmlSpace="preserve"
                                                >
                                                    <style type="text/css" />
                                                    <g id="view_1_">
                                                        <path d="M45,25c0,0-8.8,13.6-20,13.6S5,25,5,25s8.8-13.6,20-13.6S45,25,45,25z" />
                                                        <circle cx="25" cy="25" r="6.8" />
                                                    </g>
                                                    {!showPassword && (
                                                        <line className="st0" x1="6" y1="9.2" x2="43.5" y2="40.4" />
                                                    )}
                                                </svg>
                                            </a>
                                        </div>
                                    </div>

                                    <div className="clearfix mb-4 checkbox form-group">
                                        <div className="form-check float-start hide">
                                            <input className="form-check-input" type="checkbox" id="rememberme" />
                                            <label className="form-check-label text-muted" htmlFor="rememberme">
                                                Remember me
                                            </label>
                                        </div>
                                        <a href="/forgot-password" className="float-end forgot-password">
                                            Forgot Password?
                                        </a>
                                    </div>

                                    <div className="d-grid">
                                        <button
                                            className="btn btn-lg btn-primary text-uppercase login-btn login-submit"
                                            type="submit"
                                            disabled={loading}
                                        >
                                            {loading ? (
                                                <>Logging <i className="fa fa-circle-o-notch fa-spin" /></>
                                            ) : (
                                                'Login'
                                            )}
                                        </button>
                                    </div>

                                    <div className={`extra-login clearfix ${prefs?.isClient ? 'hide' : ''}`}>
                                        <span>Or</span>
                                    </div>

                                    <SocialOauthButtons
                                        mode="signin"
                                        googleHref={googleOauthUrl}
                                        microsoftHref={withOAuthReturn(buildMicrosoftOauthHref(deps.appURL || ''))}
                                        hideGoogle={!!prefs?.isClient}
                                        hideMicrosoft={!!prefs?.isClient}
                                        hideShopify={!!prefs?.isClient}
                                        forceNavigate
                                    />
                                </form>

                                <footer
                                    className={`copyright pt-4 lh-lg text-muted ${prefs?.isClient ? 'hide' : ''}`}
                                >
                                    &#169;{new Date().getFullYear()} EngageBay, Inc. All Rights Reserved.
                                    <br />
                                    <a href="https://www.engagebay.com/legal/privacy-policy" target="_blank" rel="noreferrer">
                                        Privacy Policy<span className="open-new-tab-icon" style={{ marginLeft: '3px' }} />
                                    </a>
                                </footer>
                            </div>
                        </div>

                        <ShopifyAuthModal mode="signin" onSubmit={handleShopifySubmit} />
                    </div>
                </div>
            </section>
        </>
    );
};

export default Login;

// --- Fingerprint helpers (module-level, not component state) ---

let fpRetryCount = 0;

function fillBrowserFingerprint(ref: React.MutableRefObject<string>) {
    const FP2 = (window as any).Fingerprint2;
    if (!FP2) {
        if (++fpRetryCount > 5) return;
        setTimeout(() => fillBrowserFingerprint(ref), 500);
        return;
    }
    const options = {
        excludes: {
            adBlock: true, screenResolution: true, availableScreenResolution: true,
            enumerateDevices: true, pixelRatio: true, doNotTrack: true,
            timezone: true, language: true, colorDepth: true,
            deviceMemory: true, hardwareConcurrency: true,
            sessionStorage: false, localStorage: false, indexedDb: false,
            openDatabase: false, addBehavior: false, plugins: false,
            canvas: false, webgl: false, audio: false, fonts: false,
            navigator: false, regularPlugins: false, webglVendorAndRenderer: false,
            hasLiedLanguages: false, hasLiedResolution: false, hasLiedOs: false,
            hasLiedBrowser: false, touchSupport: false, jsFonts: false,
        },
    };
    FP2.get(options, (components: any[]) => {
        const values = components.map((c: any) => c.value);
        ref.current = FP2.x64hash128(values.join(''), 31);
    });
}
